import React from "react";
import WebProfitSpinner from "../system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { projectsApi } from "../../api/projects";
import ContextualHelp from "./ContextualHelp";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const DOMAIN_PHASE = {
  requested: {
    label: "Dominio solicitado",
    severity: "info",
    description: "WebProfit recibió el dominio y está preparando su asociación con el servicio publicado.",
  },
  dns_pending: {
    label: "DNS pendiente",
    severity: "warning",
    description: "Los registros públicos todavía no coinciden con los valores requeridos.",
  },
  render_pending: {
    label: "Render pendiente",
    severity: "info",
    description: "El DNS ya está encaminado, pero Render todavía no confirmó completamente el dominio.",
  },
  certificate_pending: {
    label: "Certificado pendiente",
    severity: "info",
    description: "Render ya reconoció el dominio y está terminando de emitir o validar HTTPS.",
  },
  active: {
    label: "Activo",
    severity: "success",
    description: "El dominio está publicado, resuelve correctamente y tiene HTTPS activo.",
  },
  recoverable_error: {
    label: "Error recuperable",
    severity: "error",
    description: "La configuración se conserva. Corregí el dato indicado o reintentá la verificación.",
  },
};

const DOMAIN_FLOW = [
  { key: "requested", label: "Solicitado", detail: "Pedido recibido" },
  { key: "dns_pending", label: "DNS", detail: "Registros públicos" },
  { key: "render_pending", label: "Render", detail: "Validación del proveedor" },
  { key: "certificate_pending", label: "Certificado", detail: "Protección HTTPS" },
  { key: "active", label: "Activo", detail: "URL publicada" },
];

function clean(value) {
  return String(value ?? "").trim();
}

async function copyText(value) {
  const text = clean(value);
  if (!text) throw new Error("No hay un valor disponible para copiar.");

  let clipboardError = null;
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      clipboardError = error;
    }
  }

  if (typeof document === "undefined") {
    throw new Error("El portapapeles no está disponible en este dispositivo.");
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  try {
    const copied = document.execCommand("copy");
    if (!copied) {
      throw clipboardError || new Error("No se pudo copiar automáticamente.");
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

function dnsRecordData(record = {}) {
  return {
    type: clean(record.type || record.recordType || "DNS").toUpperCase(),
    name: clean(record.name || record.host || record.hostname || "@"),
    value: clean(record.value || record.target || record.data),
    purpose: clean(record.purpose).toLowerCase(),
  };
}

function DnsRecordField({ label, value, copyId, copiedId, onCopy }) {
  const available = Boolean(clean(value));
  const copied = copyId === copiedId;

  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.1,
        borderRadius: 2,
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
        bgcolor: "var(--wp-panel-surface, #fff)",
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 950, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="space-between">
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            fontWeight: 850,
            color: BRAND.dark,
            overflowWrap: "anywhere",
            fontFamily: label === "Tipo" ? "inherit" : "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {available ? value : "No informado"}
        </Typography>
        <Button
          type="button"
          size="small"
          variant="text"
          startIcon={<ContentCopyRoundedIcon fontSize="small" />}
          disabled={!available}
          onClick={() => onCopy(copyId, label, value)}
          aria-label={`Copiar ${label.toLowerCase()}: ${available ? value : "no disponible"}`}
          sx={{
            flexShrink: 0,
            minWidth: 84,
            px: 0.7,
            textTransform: "none",
            fontWeight: 900,
          }}
        >
          {copied ? "Copiado" : "Copiar"}
        </Button>
      </Stack>
    </Box>
  );
}

function projectKey(project) {
  return project?.slug || project?.id || project?._id || "";
}

function canonicalStateName(state) {
  return clean(state?.name).toLowerCase().replace(/\.$/, "");
}

function uniqueDomainStates(states = []) {
  const byName = new Map();
  (Array.isArray(states) ? states : []).forEach((state) => {
    const name = canonicalStateName(state);
    if (name) byName.set(name, { ...state, name });
  });
  return Array.from(byName.values());
}

function projectDomainStates(project) {
  return uniqueDomainStates(project?.deployment?.customDomains || []);
}

function initialDomain(project) {
  const states = projectDomainStates(project);
  const active = Array.isArray(project?.domains)
    ? project.domains.map((domain) => clean(domain).toLowerCase()).find(Boolean)
    : "";
  return active || canonicalStateName(states[0]) || "";
}

function validateDomainInput(value) {
  const raw = clean(value);
  if (!raw) {
    return { domain: "", error: "Ingresá el dominio que querés conectar." };
  }
  if (raw.length > 2048) {
    return { domain: "", error: "La dirección ingresada es demasiado larga." };
  }
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) {
    return { domain: "", error: "El dominio contiene caracteres no permitidos." };
  }

  const schemeMatch = raw.match(/^([a-z][a-z\d+.-]*):/i);
  if (schemeMatch && !/^https?:\/\//i.test(raw)) {
    return { domain: "", error: "Usá un dominio o una dirección http/https válida." };
  }

  const candidate = /^https?:\/\//i.test(raw)
    ? raw
    : raw.startsWith("//")
      ? `https:${raw}`
      : `https://${raw}`;
  const authority = candidate
    .replace(/^https?:\/\//i, "")
    .split(/[/?#]/, 1)[0];

  if (!authority || /[%\s<>\[\]{}"'`]/.test(authority)) {
    return { domain: "", error: "El hostname contiene caracteres inseguros o no válidos." };
  }

  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    return { domain: "", error: "No pudimos reconocer un hostname válido." };
  }

  if (parsed.username || parsed.password) {
    return { domain: "", error: "No incluyas usuario ni contraseña en el dominio." };
  }
  if (parsed.port) {
    return { domain: "", error: "No incluyas un puerto; ingresá solamente el dominio público." };
  }

  const hostname = clean(parsed.hostname).toLowerCase().replace(/\.$/, "");
  if (!hostname || hostname.endsWith(".") || hostname.length > 253) {
    return { domain: "", error: "El hostname está vacío, mal formado o supera el largo permitido." };
  }
  if (hostname === "localhost" || hostname.includes(":")) {
    return { domain: "", error: "Ingresá un dominio público, no localhost ni una dirección IPv6." };
  }
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
    return { domain: "", error: "Ingresá un dominio público, no una dirección IP." };
  }

  const labels = hostname.split(".");
  if (labels.length < 2 || labels.some((label) => !label)) {
    return { domain: "", error: "Ingresá un dominio completo, por ejemplo negocio.com." };
  }
  if (
    labels.some(
      (label) =>
        label.length > 63 ||
        !/^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/i.test(label),
    )
  ) {
    return { domain: "", error: "El dominio contiene caracteres o etiquetas no válidas." };
  }

  return {
    domain: hostname,
    error: "",
    normalized: hostname !== raw.toLowerCase().replace(/\.$/, ""),
  };
}

function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function remoteDomainStatus(data) {
  return (
    objectValue(data?.status) ||
    objectValue(data?.configuration?.status) ||
    objectValue(data?.verification?.status) ||
    null
  );
}

function responseDnsCheck(data) {
  return (
    objectValue(data?.verification?.dnsCheck) ||
    objectValue(data?.configuration?.dnsCheck) ||
    null
  );
}

function domainStateFromResponse(data, preferredDomain = "") {
  const persisted = objectValue(data?.state) || {};
  const remote = remoteDomainStatus(data);
  const dnsCheck = responseDnsCheck(data);
  const name = clean(
    data?.domain || persisted?.name || remote?.name || preferredDomain,
  )
    .toLowerCase()
    .replace(/\.$/, "");

  if (!name) return null;

  const responseActive = typeof data?.active === "boolean" ? data.active : undefined;
  const remoteDomain = objectValue(remote?.domain);
  const remoteHttps = objectValue(remote?.https);

  return {
    ...persisted,
    name,
    ...(clean(remote?.status) ? { status: clean(remote.status).toLowerCase() } : {}),
    ...(remoteDomain
      ? {
          renderRegistered: remoteDomain.registered === true,
          renderVerified: remoteDomain.verified === true,
          verificationStatus:
            clean(remoteDomain.verificationStatus) || persisted.verificationStatus,
          renderDomainId: clean(remoteDomain.id) || persisted.renderDomainId,
        }
      : {}),
    ...(remoteHttps
      ? {
          httpsStatus: clean(remoteHttps.status).toLowerCase(),
          httpsValid: remoteHttps.valid === true,
          httpsErrorCode: clean(remoteHttps.errorCode),
          httpsErrorMessage: clean(remoteHttps.errorMessage),
        }
      : {}),
    ...(dnsCheck
      ? {
          dnsStatus: clean(dnsCheck.status).toLowerCase() || persisted.dnsStatus,
          dnsMatched: dnsCheck.matched === true,
          dnsReason: clean(dnsCheck.reason) || persisted.dnsReason,
          dnsCheckedAt: dnsCheck.checkedAt || persisted.dnsCheckedAt,
        }
      : {}),
    checkedAt: remote?.checkedAt || persisted.checkedAt,
    active:
      responseActive !== undefined
        ? responseActive
        : typeof persisted.active === "boolean"
          ? persisted.active
          : remote?.ready === true,
  };
}

function domainSignals(state, activeDomains = []) {
  const status = clean(state?.status).toLowerCase();
  const verificationStatus = clean(state?.verificationStatus).toLowerCase();
  const dnsStatus = clean(state?.dnsStatus).toLowerCase();
  const httpsStatus = clean(state?.httpsStatus).toLowerCase();
  const name = canonicalStateName(state);
  const activeDomainNames = (Array.isArray(activeDomains) ? activeDomains : [])
    .map((domain) => clean(domain).toLowerCase().replace(/\.$/, ""))
    .filter(Boolean);

  let renderRegistered = null;
  if (typeof state?.renderRegistered === "boolean") {
    renderRegistered = state.renderRegistered;
  } else if (clean(state?.renderDomainId)) {
    renderRegistered = true;
  } else if (status === "not_registered") {
    renderRegistered = false;
  } else if (["pending_dns", "pending_https", "live", "https_error", "dns_error"].includes(status)) {
    renderRegistered = true;
  }

  let renderVerified = null;
  if (typeof state?.renderVerified === "boolean") {
    renderVerified = state.renderVerified;
  } else if (verificationStatus) {
    renderVerified = verificationStatus === "verified";
  } else if (["pending_https", "live", "https_error"].includes(status)) {
    renderVerified = true;
  }

  const dnsMatched = state?.dnsMatched === true || dnsStatus === "matched";
  const httpsActive = state?.httpsValid === true || httpsStatus === "active" || status === "live";
  const active = state?.active === true || Boolean(name && activeDomainNames.includes(name));
  const hasError =
    ["dns_error", "https_error", "failed"].includes(status) ||
    dnsStatus === "error" ||
    Boolean(clean(state?.errorCode) || clean(state?.errorMessage));

  return {
    status,
    verificationStatus,
    dnsStatus,
    httpsStatus,
    dnsMatched,
    renderRegistered,
    renderVerified,
    httpsActive,
    active,
    hasError,
  };
}

function domainPhase(state, activeDomains = []) {
  if (!state) return { key: "requested", ...DOMAIN_PHASE.requested };

  const signals = domainSignals(state, activeDomains);
  let key = "requested";

  if (signals.hasError) {
    key = "recoverable_error";
  } else if (signals.active && signals.httpsActive) {
    key = "active";
  } else if (signals.status === "registering") {
    key = "requested";
  } else if (signals.status === "not_registered" || signals.renderRegistered === false) {
    key = "render_pending";
  } else if (!signals.dnsMatched) {
    key = "dns_pending";
  } else if (signals.renderVerified !== true) {
    key = "render_pending";
  } else if (!signals.httpsActive) {
    key = "certificate_pending";
  } else if (signals.status === "live" && !signals.active) {
    key = "render_pending";
  } else if (signals.active) {
    key = "active";
  } else {
    key = "render_pending";
  }

  let description = DOMAIN_PHASE[key].description;
  if (key === "render_pending") {
    if (signals.renderRegistered === false || signals.status === "not_registered") {
      description = "WebProfit ya envió la solicitud, pero Render todavía no devuelve el dominio asociado al servicio.";
    } else if (signals.dnsMatched && signals.renderVerified !== true) {
      description = "El DNS ya coincide. Render todavía está validando la propiedad del dominio.";
    } else if (signals.httpsActive && !signals.active) {
      description = "Render ya reconoce el dominio; falta completar su activación dentro de WebProfit.";
    }
  } else if (key === "recoverable_error") {
    if (signals.status === "dns_error" || signals.dnsStatus === "error") {
      description = "No se pudo consultar DNS en este intento. La configuración sigue guardada y puede verificarse nuevamente.";
    } else if (signals.status === "https_error") {
      description = "Render reconoce el dominio, pero el certificado HTTPS necesita una nueva validación.";
    } else {
      description = "La operación no terminó, pero el dominio sigue guardado y puede reintentarse sin empezar de cero.";
    }
  }

  return { key, ...DOMAIN_PHASE[key], description, signals };
}

function domainProgressIndex(state, phase) {
  const directIndex = DOMAIN_FLOW.findIndex((step) => step.key === phase?.key);
  if (directIndex >= 0) return directIndex;

  const signals = phase?.signals || domainSignals(state);
  if (signals.renderVerified === true || signals.status === "https_error") return 3;
  if (signals.dnsMatched) return 2;
  if (clean(state?.dnsTarget) || (Array.isArray(state?.dnsRecords) && state.dnsRecords.length)) return 1;
  return 0;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function apiMethod(name) {
  const method = projectsApi?.[name];
  if (typeof method === "function") return method.bind(projectsApi);
  const error = new Error("La API de dominios todavía no está disponible en este panel.");
  error.code = "DOMAIN_API_NOT_AVAILABLE";
  throw error;
}

export default function ProjectCustomDomainCard({ project, canManage = true, onProjectChange, compact = false, readOnlyMessage = "" }) {
  const key = projectKey(project);
  const [domainInput, setDomainInput] = React.useState(() => initialDomain(project));
  const [selectedDomain, setSelectedDomain] = React.useState(() => initialDomain(project));
  const [states, setStates] = React.useState(() => projectDomainStates(project));
  const [activeDomains, setActiveDomains] = React.useState(() =>
    Array.isArray(project?.domains) ? project.domains : [],
  );
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [copiedDnsField, setCopiedDnsField] = React.useState("");
  const [dnsCopyFeedback, setDnsCopyFeedback] = React.useState(null);
  const [technicalExpanded, setTechnicalExpanded] = React.useState(false);
  const copyResetTimer = React.useRef(null);

  const validation = React.useMemo(() => validateDomainInput(domainInput), [domainInput]);
  const currentDomain = validation.domain || selectedDomain;
  const currentState = React.useMemo(
    () => states.find((state) => canonicalStateName(state) === currentDomain) || null,
    [currentDomain, states],
  );
  const phase = React.useMemo(
    () => domainPhase(currentState, activeDomains),
    [activeDomains, currentState],
  );
  const meta = phase;
  const signals = phase.signals || domainSignals(currentState, activeDomains);
  const progressIndex = domainProgressIndex(currentState, phase);
  const dnsRecords = Array.isArray(currentState?.dnsRecords) ? currentState.dnsRecords : [];
  const checkedAt = formatDate(currentState?.dnsCheckedAt || currentState?.checkedAt);
  const isManaged = Boolean(currentState);
  const isActive = phase.key === "active";
  const recoverableError = clean(
    currentState?.errorMessage || currentState?.httpsErrorMessage,
  );
  const needsDnsAction = phase.key === "dns_pending" || phase.key === "recoverable_error";
  const showTechnicalDetails = !compact || technicalExpanded || needsDnsAction;

  const applyProject = React.useCallback(
    (nextProject) => {
      if (!nextProject) return;
      setStates(projectDomainStates(nextProject));
      setActiveDomains(Array.isArray(nextProject.domains) ? nextProject.domains : []);
      if (typeof onProjectChange === "function") onProjectChange(nextProject);
    },
    [onProjectChange],
  );

  const applyDomainResponse = React.useCallback(
    (data, preferredDomain = "") => {
      if (!data) return;
      if (data.project) applyProject(data.project);

      const responseState = domainStateFromResponse(data, preferredDomain);
      if (Array.isArray(data.domains)) {
        setStates(uniqueDomainStates(data.domains));
      } else if (responseState) {
        setStates((current) =>
          uniqueDomainStates([
            ...current.filter(
              (state) => canonicalStateName(state) !== canonicalStateName(responseState),
            ),
            responseState,
          ]),
        );
      }
      if (Array.isArray(data.activeDomains)) setActiveDomains(data.activeDomains);

      const nextDomain = clean(
        responseState?.name || data.domain || preferredDomain,
      ).toLowerCase().replace(/\.$/, "");
      if (nextDomain) {
        setSelectedDomain(nextDomain);
        setDomainInput(nextDomain);
      }
    },
    [applyProject],
  );

  React.useEffect(() => {
    const nextStates = projectDomainStates(project);
    setStates(nextStates);
    setActiveDomains(Array.isArray(project?.domains) ? project.domains : []);

    const fallback = initialDomain(project);
    if (!touched && fallback) {
      setDomainInput(fallback);
      setSelectedDomain(fallback);
    }
  }, [project, touched]);

  React.useEffect(
    () => () => {
      if (copyResetTimer.current) window.clearTimeout(copyResetTimer.current);
    },
    [],
  );

  React.useEffect(() => {
    if (!key) return undefined;
    let active = true;

    const load = async () => {
      try {
        const data = await apiMethod("getDomain")(key);
        if (active) applyDomainResponse(data);
      } catch {
        // La carga inicial es silenciosa: el estado persistido del proyecto sigue visible.
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [applyDomainResponse, key]);

  const run = React.useCallback(
    async (operation, action) => {
      if (!key || busy) return null;

      setTouched(true);
      setError("");
      setNotice("");
      setBusy(operation);
      try {
        const data = await action();
        applyDomainResponse(data, validation.domain || selectedDomain);
        return data;
      } catch (requestError) {
        setError(requestError?.message || "No se pudo completar la operación del dominio.");
        return null;
      } finally {
        setBusy("");
      }
    },
    [applyDomainResponse, busy, key, selectedDomain, validation.domain],
  );

  const copyDnsField = React.useCallback(async (copyId, label, value) => {
    if (copyResetTimer.current) window.clearTimeout(copyResetTimer.current);

    try {
      await copyText(value);
      setCopiedDnsField(copyId);
      setDnsCopyFeedback({
        severity: "success",
        message: `${label} copiado. Pegalo en el campo equivalente de tu proveedor DNS.`,
      });
      copyResetTimer.current = window.setTimeout(() => {
        setCopiedDnsField("");
        setDnsCopyFeedback(null);
      }, 3000);
    } catch (copyError) {
      setCopiedDnsField("");
      setDnsCopyFeedback({
        severity: "error",
        message:
          copyError?.message ||
          "No se pudo copiar automáticamente. Seleccioná el dato y copialo manualmente.",
      });
    }
  }, []);

  const saveDomain = async () => {
    if (!canManage || busy) return;
    setTouched(true);
    if (validation.error) {
      setError(validation.error);
      return;
    }

    const data = await run("request", () =>
      apiMethod("requestDomain")(key, { domain: validation.domain }),
    );
    if (data) {
      setNotice(
        data?.state?.active
          ? "El dominio quedó activo y protegido con HTTPS."
          : "Dominio guardado. Seguí las indicaciones DNS y verificá cuando los cambios se propaguen.",
      );
    }
  };

  const refreshDomain = async () => {
    setTouched(true);
    if (domainInput && validation.error) {
      setError(validation.error);
      return;
    }

    const requestedDomain = validation.domain || selectedDomain;
    const requestedIsManaged = Boolean(
      requestedDomain &&
      states.some((state) => canonicalStateName(state) === requestedDomain),
    );

    if (requestedDomain && !requestedIsManaged) {
      setNotice("");
      setError("Ese dominio todavía no está conectado. Presioná Guardar dominio antes de actualizar su estado.");
      return;
    }

    const data = await run("refresh", () =>
      apiMethod("getDomain")(key, requestedDomain ? { domain: requestedDomain } : {}),
    );
    if (data) setNotice("Estado actualizado con la información más reciente.");
  };

  const verifyDomain = async () => {
    if (!canManage) return;
    setTouched(true);
    if (validation.error) {
      setError(validation.error);
      return;
    }

    const data = await run("verify", () =>
      apiMethod("verifyDomain")(key, { domain: validation.domain }),
    );
    if (data) {
      setNotice(
        data?.state?.active
          ? "DNS verificado: el dominio ya está activo."
          : "Verificación completada. Si acabás de cambiar DNS, la propagación puede demorar.",
      );
    }
  };

  const unlinkDomain = async () => {
    if (!canManage) return;
    setTouched(true);
    if (validation.error) {
      setError(validation.error);
      return;
    }

    const confirmed = window.confirm(
      `¿Desvincular ${validation.domain}? La web original del proyecto seguirá disponible.`,
    );
    if (!confirmed) return;

    const data = await run("unlink", () =>
      apiMethod("unlinkDomain")(key, { domain: validation.domain }),
    );
    if (data) {
      const remainingStates = Array.isArray(data.domains)
        ? uniqueDomainStates(data.domains)
        : states.filter((state) => canonicalStateName(state) !== validation.domain);
      const nextDomain = canonicalStateName(remainingStates[0]);
      setStates(remainingStates);
      setSelectedDomain(nextDomain);
      setDomainInput(nextDomain);
      setNotice("El dominio fue desvinculado sin afectar el servicio ni los demás dominios.");
    }
  };

  const inputHelper = touched && validation.error
    ? validation.error
    : validation.normalized && validation.domain
      ? `Se guardará como ${validation.domain}`
      : "Podés pegar dominio.com o una URL completa; se usará solamente el hostname.";

  return (
    <Paper
      component="section"
      elevation={0}
      variant="outlined"
      sx={{
        p: compact ? { xs: 1.25, sm: 1.5 } : { xs: 2, md: 2.4 },
        borderRadius: 4,
        borderColor: isActive
          ? "rgba(1,142,66,.24)"
          : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
        bgcolor: isActive
          ? "rgba(1,142,66,.025)"
          : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.015)",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.3}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: compact ? 38 : 44,
                height: compact ? 38 : 44,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                color: isActive ? "success.main" : BRAND.orange,
                bgcolor: isActive
                  ? "rgba(1,142,66,.10)"
                  : "rgba(243,90,58,.10)",
                flexShrink: 0,
              }}
            >
              {isActive ? <CheckCircleRoundedIcon /> : <LanguageRoundedIcon />}
            </Box>
            <Box>
              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Dominio personalizado
                </Typography>
                {currentState ? (
                  <Chip
                    size="small"
                    label={meta.label}
                    color={meta.severity}
                    variant={isActive ? "filled" : "outlined"}
                    sx={{ fontWeight: 900 }}
                  />
                ) : null}
              </Stack>
              {!compact ? (
                <Typography variant="body2" sx={{ mt: 0.2, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                  Usá tu propio dominio sin perder la dirección original de WebProfit.
                </Typography>
              ) : (
                <ContextualHelp
                  compact
                  title="Dominio personalizado"
                  text="La URL original de WebProfit sigue disponible. Para usar tu dominio propio, cargalo acá y copiá los registros DNS que WebProfit indique."
                  bullets={["No elimines registros ajenos al sitio salvo que el panel lo indique.", "HTTPS se activa después de que el DNS coincida."]}
                />
              )}
            </Box>
          </Stack>

          <Button
            type="button"
            size="small"
            startIcon={busy === "refresh" ? <WebProfitSpinner size={15} /> : <RefreshRoundedIcon />}
            disabled={!key || Boolean(busy)}
            onClick={refreshDomain}
            sx={{ alignSelf: { xs: "stretch", sm: "center" }, width: { xs: compact ? "100%" : "auto", sm: "auto" }, textTransform: "none", fontWeight: 850 }}
          >
            {compact ? "Actualizar" : "Actualizar estado"}
          </Button>
        </Stack>

        {states.length > 1 ? (
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {states.map((state) => {
              const name = canonicalStateName(state);
              const selected = name === currentDomain;
              return (
                <Chip
                  key={name}
                  clickable
                  label={name}
                  color={state.active ? "success" : selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  onClick={() => {
                    setTouched(true);
                    setDomainInput(name);
                    setSelectedDomain(name);
                    setError("");
                    setNotice("");
                  }}
                  sx={{ fontWeight: 800 }}
                />
              );
            })}
          </Stack>
        ) : null}

        {canManage ? (
          <Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ md: "flex-start" }}>
            <TextField
              fullWidth
              label="Dominio"
              value={domainInput}
              placeholder="negocio.com"
              autoComplete="url"
              error={Boolean(touched && validation.error)}
              helperText={inputHelper}
              onBlur={() => setTouched(true)}
              onChange={(event) => {
                setTouched(false);
                setDomainInput(event.target.value);
                setError("");
                setNotice("");
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                saveDomain();
              }}
              inputProps={{
                inputMode: "url",
                spellCheck: "false",
                "aria-label": "Dominio personalizado",
              }}
            />
            <Button
              type="button"
              variant="contained"
              onClick={saveDomain}
              startIcon={busy === "request" ? <WebProfitSpinner size={17} color="inherit" /> : <SaveRoundedIcon />}
              disabled={!key || Boolean(busy)}
              sx={{
                minHeight: 56,
                width: { xs: compact ? "100%" : "auto", md: "auto" },
                px: 2.5,
                whiteSpace: "nowrap",
                borderRadius: 2.5,
                bgcolor: BRAND.orange,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "#D94729" },
              }}
            >
              Guardar dominio
            </Button>
          </Stack>
          </Box>
        ) : (
          <Alert severity="info">{readOnlyMessage || "Tu acceso permite consultar el dominio y su estado, pero no cambiar su configuración."}</Alert>
        )}

        {currentState ? (
          <Paper variant="outlined" sx={{ p: compact ? 1.25 : 1.8, borderRadius: 3, bgcolor: "var(--wp-panel-surface, #fff)" }}>
            <Stack spacing={1.4}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark, overflowWrap: "anywhere" }}>
                    {canonicalStateName(currentState)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {meta.description}
                  </Typography>
                </Box>
                {checkedAt ? (
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", whiteSpace: "nowrap" }}>
                    Última comprobación: {checkedAt}
                  </Typography>
                ) : null}
              </Stack>

              {compact ? (
                <Button
                  type="button"
                  size="small"
                  variant={needsDnsAction ? "contained" : "text"}
                  onClick={() => setTechnicalExpanded((value) => !value)}
                  endIcon={<ExpandMoreRoundedIcon sx={{ transform: showTechnicalDetails ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
                  sx={{
                    alignSelf: "stretch",
                    justifyContent: "space-between",
                    textTransform: "none",
                    fontWeight: 900,
                    bgcolor: needsDnsAction ? BRAND.orange : undefined,
                    color: needsDnsAction ? "#fff" : BRAND.dark,
                  }}
                >
                  {showTechnicalDetails ? "Ocultar configuración técnica" : dnsRecords.length ? `Ver DNS (${dnsRecords.length})` : "Ver configuración técnica"}
                </Button>
              ) : null}

              <Collapse in={showTechnicalDetails} timeout="auto" unmountOnExit={compact}>
                <Stack spacing={1.4}>
                  <Divider />

                  <Box>
                    <Typography
                  variant="caption"
                  sx={{ fontWeight: 950, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                >
                  ESTADO DE ACTIVACIÓN
                </Typography>
                <Box
                  role="list"
                  aria-label="Progreso de activación del dominio"
                  sx={{
                    mt: 0.8,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0,1fr)",
                      sm: "repeat(5, minmax(0,1fr))",
                    },
                    gap: 0.8,
                  }}
                >
                  {DOMAIN_FLOW.map((step, index) => {
                    const current = index === progressIndex;
                    const completed = phase.key === "active" ? index <= progressIndex : index < progressIndex;
                    const failed = phase.key === "recoverable_error" && current;

                    return (
                      <Box
                        key={step.key}
                        role="listitem"
                        aria-current={current ? "step" : undefined}
                        sx={{
                          minWidth: 0,
                          p: 1,
                          borderRadius: 2.2,
                          border: "1px solid",
                          borderColor: failed
                            ? "rgba(211,47,47,.34)"
                            : current
                              ? "rgba(243,90,58,.34)"
                              : completed
                                ? "rgba(1,142,66,.24)"
                                : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
                          bgcolor: failed
                            ? "rgba(211,47,47,.045)"
                            : current
                              ? "rgba(243,90,58,.055)"
                              : completed
                                ? "rgba(1,142,66,.035)"
                                : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
                        }}
                      >
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <Box
                            aria-hidden="true"
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                              fontSize: 12,
                              fontWeight: 950,
                              color: completed || current ? "#fff" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.48)",
                              bgcolor: failed
                                ? "error.main"
                                : completed
                                  ? "success.main"
                                  : current
                                    ? BRAND.orange
                                    : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
                            }}
                          >
                            {completed ? "✓" : index + 1}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 0,
                              fontWeight: current || completed ? 950 : 800,
                              color: BRAND.dark,
                            }}
                          >
                            {step.label}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 0.45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                        >
                          {failed ? "Reintento disponible" : step.detail}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<DnsRoundedIcon />}
                  label={
                    signals.dnsMatched
                      ? "DNS correcto"
                      : signals.dnsStatus === "error"
                        ? "DNS no disponible"
                        : signals.dnsStatus === "not_checked" || phase.key === "requested"
                          ? "DNS sin comprobar"
                          : "DNS pendiente"
                  }
                  color={
                    signals.dnsMatched
                      ? "success"
                      : signals.dnsStatus === "error"
                        ? "error"
                        : signals.dnsStatus === "not_checked" || phase.key === "requested"
                          ? "default"
                          : "warning"
                  }
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  icon={<LanguageRoundedIcon />}
                  label={
                    phase.key === "requested"
                      ? "Solicitud recibida"
                      : signals.renderRegistered === false
                        ? "Render registrando"
                        : signals.renderVerified
                          ? "Render verificado"
                          : "Render pendiente"
                  }
                  color={signals.renderVerified ? "success" : "info"}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  icon={<VerifiedUserRoundedIcon />}
                  label={
                    signals.httpsActive
                      ? "HTTPS activo"
                      : signals.status === "https_error"
                        ? "HTTPS con error"
                        : phase.key === "certificate_pending"
                          ? "HTTPS pendiente"
                          : "HTTPS aún no iniciado"
                  }
                  color={signals.httpsActive ? "success" : signals.status === "https_error" ? "error" : "default"}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
                {signals.active ? (
                  <Chip label="URL pública activa" color="success" sx={{ fontWeight: 800 }} />
                ) : null}
              </Stack>

              {phase.key === "recoverable_error" ? (
                <Alert severity="error">
                  <strong>Error recuperable.</strong>{" "}
                  {recoverableError || meta.description} La configuración no se eliminó; revisá los datos y volvé a verificar.
                </Alert>
              ) : null}

              {dnsRecords.length ? (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 950, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                    REGISTROS DNS REQUERIDOS
                  </Typography>
                  <Alert severity="info" sx={{ mt: 0.8, mb: 1.1 }}>
                    En el panel donde administrás tu dominio, buscá campos equivalentes a <strong>Tipo</strong>,{" "}
                    <strong>Nombre</strong> (también puede llamarse Host o Alias) y <strong>Valor</strong>{" "}
                    (Destino o Apunta a). Copiá cada dato por separado para evitar errores.
                  </Alert>
                  <Stack spacing={1.1}>
                    {dnsRecords.map((record, index) => {
                      const dns = dnsRecordData(record);
                      const recordKey = `${dns.type}-${dns.name}-${dns.value}-${index}`;
                      const removeRecord = dns.purpose === "remove";

                      return (
                        <Paper
                          key={recordKey}
                          variant="outlined"
                          sx={{
                            p: 1.2,
                            borderRadius: 2.5,
                            borderColor: removeRecord
                              ? "rgba(211,47,47,.22)"
                              : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
                            bgcolor: removeRecord
                              ? "rgba(211,47,47,.025)"
                              : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)",
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={0.7}
                              alignItems={{ sm: "center" }}
                              justifyContent="space-between"
                            >
                              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                                Registro {index + 1} de {dnsRecords.length}
                              </Typography>
                              <Chip
                                size="small"
                                label={removeRecord ? "ELIMINAR" : "AGREGAR O ACTUALIZAR"}
                                color={removeRecord ? "error" : "info"}
                                variant="outlined"
                                sx={{ alignSelf: { xs: "flex-start", sm: "center" }, fontWeight: 900 }}
                              />
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                              {removeRecord
                                ? "Eliminá este registro si existe. No crees uno nuevo con estos datos."
                                : "Creá este registro o actualizalo para que coincida exactamente con estos datos."}
                            </Typography>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "minmax(0,1fr)",
                                  md: "minmax(150px,.7fr) minmax(210px,1fr) minmax(260px,1.5fr)",
                                },
                                gap: 0.9,
                              }}
                            >
                              <DnsRecordField
                                label="Tipo"
                                value={dns.type}
                                copyId={`${recordKey}:type`}
                                copiedId={copiedDnsField}
                                onCopy={copyDnsField}
                              />
                              <DnsRecordField
                                label="Nombre"
                                value={dns.name}
                                copyId={`${recordKey}:name`}
                                copiedId={copiedDnsField}
                                onCopy={copyDnsField}
                              />
                              <DnsRecordField
                                label="Valor"
                                value={dns.value}
                                copyId={`${recordKey}:value`}
                                copiedId={copiedDnsField}
                                onCopy={copyDnsField}
                              />
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                  {dnsCopyFeedback ? (
                    <Alert
                      severity={dnsCopyFeedback.severity}
                      role="status"
                      onClose={() => {
                        setDnsCopyFeedback(null);
                        setCopiedDnsField("");
                      }}
                      sx={{ mt: 1.1 }}
                    >
                      {dnsCopyFeedback.message}
                    </Alert>
                  ) : null}
                </Box>
              ) : currentState.dnsTarget ? (
                <Alert severity="info">
                  Destino DNS informado por Render: <strong>{currentState.dnsTarget}</strong>
                </Alert>
              ) : null}

              {currentState.dnsReason && !currentState.dnsMatched ? (
                <Typography variant="body2" color="text.secondary">
                  {currentState.dnsReason}
                </Typography>
              ) : null}

              {currentState.errorMessage && phase.key !== "recoverable_error" ? (
                <Alert severity="error">{currentState.errorMessage}</Alert>
              ) : null}

                  {canManage ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end" sx={{ "& .MuiButton-root": { width: { xs: compact ? "100%" : "auto", sm: "auto" } } }}>
                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={busy === "verify" ? <WebProfitSpinner size={16} /> : <VerifiedUserRoundedIcon />}
                        disabled={!key || Boolean(busy)}
                        onClick={verifyDomain}
                        sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
                      >
                        Verificar DNS
                      </Button>
                      <Button
                        type="button"
                        color="error"
                        startIcon={busy === "unlink" ? <WebProfitSpinner size={16} color="inherit" /> : <DeleteOutlineRoundedIcon />}
                        disabled={!key || Boolean(busy)}
                        onClick={unlinkDomain}
                        sx={{ textTransform: "none", fontWeight: 850, borderRadius: 999 }}
                      >
                        Desvincular
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              </Collapse>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="info">
            Conectá un dominio que controles. WebProfit te mostrará exactamente qué registros DNS configurar y conservará disponible la URL original del proyecto.
          </Alert>
        )}

        {notice ? <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert> : null}
        {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      </Stack>
    </Paper>
  );
}
